import React, { useRef, useState } from "react";
import Grid from '@mui/material/Grid2';
import { TicketIssue } from "@services/tickets/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { displayDateTime } from "@common/utils/localTime.utils";
import { FormControl, styled, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import ConsoleLog from "@pages/Tickets/ConsoleLog";
import Typography from "@common/components/Typography";
import IconCardButton from "@common/components/IconCardButton";
import { useContext, useEffect } from "react";
import { getIssue } from "@services/account";
import MenuItem from "@common/components/MenuItem";
import Select from "@common/components/Select";
import { getDisplayName, getProducers } from "@services/user";
import { User } from "@services/user/types";
import { FormContext } from "../../context/formContext";
import { Room } from "@assets/icons";
import { LocationCity, Work } from "@mui/icons-material";

export enum TicketsType {
  "Meeting",
  "Device",
}
type TicketAdvancedDetailsProps = {
  ticketType: TicketsType;
};

type IssueSourceState = {
  issueSource: string;
  issueSources: string[];
};

type producerState = {
  producer: string;
  producers: User[];
};

const StyledTypo = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.grey.g9B9B9B,
}));

const TicketAdvancedDetails = (props: TicketAdvancedDetailsProps) => {
  const { ticketType } = props;
  const { t } = useTranslation();
  const { dispatchTicket, dispatchHasChanged, form, canEdit } =
    useContext(FormContext);
  const { ticket } = form;
  const disabled = ticket.status === "Closed" || !canEdit;
  const [user] = useAuthenticatedUser();
  const selectRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState(0);
  const [{ issueSource, issueSources }, dispatch] = React.useReducer(
    (state: IssueSourceState, action: IssueSourceState) => ({
      ...state,
      ...action,
    }),
    {
      issueSource: "",
      issueSources: [],
    }
  );

  const [{ producer, producers }, dispatchProducer] = React.useReducer(
    (state: producerState, action: producerState) => ({
      ...state,
      ...action,
    }),
    {
      producer: "",
      producers: [],
    }
  );

  const fetchSources = async (accountId: string) => {
    let issueSources = await getIssue(accountId, "source");
    if (!issueSources.find((issue) => issue == ticket.issue?.source)) {
      issueSources.push(ticket.issue?.source ?? "");
    }
    dispatch({ issueSources, issueSource: ticket.issue?.source ?? "" });
  };
  const fetchProducers = async (accountId: string) => {
    let producers = await getProducers(accountId);
    let canProduceMeeting = user?.role.rolePermissions.produceMeetings ?? false;

    if (canProduceMeeting) {
      if (!producers.find((producer) => producer.email == user?.email)) {
        producers.push(user as User);
      }
    }

    if (ticket?.assignedTo)
      if (!producers.find((prod) => prod.email === ticket?.assignedTo)) {
        producers.push({ id: "", email: ticket?.assignedTo } as User);
      }

    producers.sort(function (a, b) {
      if ((a?.email ?? "").toLowerCase() < (b?.email ?? "").toLowerCase())
        return -1;
      if ((a?.email ?? "").toLowerCase() > (b?.email ?? "").toLowerCase())
        return 1;
      return 0;
    });

    dispatchProducer({
      producers,
      producer:
        ticket?.assignedTo ??
        (!Boolean(ticket?.id) &&
        ticketType === TicketsType.Meeting &&
        user?.email
          ? user?.email
          : ""),
    });
  };

  useEffect(() => {
    if (ticket?.account !== undefined) {
      setMaxWidth(selectRef.current?.getBoundingClientRect().width ?? 0);
      fetchSources(ticket.account.id);
      fetchProducers(ticket.account.id);
    }
  }, [ticket.account]);

  const getTimeOfIssue = () => {
    if (ticket?.issue?.ticketIssueTime) {
      return displayDateTime(ticket.issue.ticketIssueTime);
    } else if (
      ticket &&
      ticket.details &&
      ticket.details.events &&
      ticket.details.events.length > 0
    ) {
      const sortedEvents = ticket.details.events.sort(
        (a, b) => a.createDate - b.createDate
      );

      if (sortedEvents[0].priority != "Informational")
        return displayDateTime(sortedEvents[0].createDate);
    }
    return t("Unknown");
  };

  const timeOfIssueConverted = getTimeOfIssue();

  const handleIssueChange = (value: string, field: keyof TicketIssue) => {
    if (field == "source") {
      dispatch({
        issueSources,
        issueSource: value,
      });
      let issue = { ...ticket.issue, ...{ source: value } };
      dispatchTicket({ issue: issue as TicketIssue });
    }
  };

  const handleProducerChange = (value: string) => {
    dispatchProducer({
      producers,
      producer: value,
    });
    dispatchTicket({ assignedTo: value });
  };

  return (
    <Grid
      container
      sx={(theme) => ({
        padding: theme.spacing(2, 4),
      })}
      spacing={2}
    >
      <Grid
        sx={{
          flex: "1"
        }}>
        <Grid container spacing={4} sx={{ width: '100%' }}>
          <Grid>{t("Details")}</Grid>
          <Grid
            sx={{
              flex: "1"
            }}>
            <Grid container spacing={2} sx={{ width: '100%', flexDirection: "column" }}>
              <Grid>
                <IconCardButton
                  label={ticket.account?.name ?? ""}
                  icon={<Work />}
                  disabled
                />
              </Grid>
              <Grid>
                <IconCardButton
                  label={ticket.room?.location?.name ?? ""}
                  icon={<LocationCity />}
                  disabled
                />
              </Grid>
              <Grid>
                <IconCardButton
                  label={ticket.room?.name ?? ""}
                  icon={<Room />}
                  disabled
                />
              </Grid>
              <Grid>
                <FormControl fullWidth size="small">
                  <Select
                    labelId="inputIssueSourceLabel"
                    id="inputIssueSource"
                    value={issueSource}
                    label={t("Source")}
                    onChange={(e) => {
                      handleIssueChange(e.target.value as string, "source");
                      dispatchHasChanged(true);
                    }}
                    disabled={disabled}
                    shrink
                    displayEmpty
                  >
                    <MenuItem key="source_none" value=""></MenuItem>
                    {issueSources?.map((type) => {
                      return (
                        <MenuItem key={`source_${type}`} value={type}>
                          {type}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <FormControl fullWidth size="small" ref={selectRef}>
                  <Select
                    labelId="inputProducerLabel"
                    id="inputProducer"
                    value={producer.toLocaleLowerCase()}
                    label={t("Assignee")}
                    disabled={disabled}
                    onChange={(e) => {
                      handleProducerChange(e.target.value as string);
                      dispatchHasChanged(true);
                    }}
                    displayEmpty
                    shrink
                    sx={{
                      maxWidth: maxWidth,
                    }}
                  >
                    <MenuItem key="producer_none" value=""></MenuItem>
                    {producers?.map((user) => {
                      return (
                        <MenuItem
                          key={`producer_${user.email}`}
                          value={user.email?.toLocaleLowerCase()}
                        >
                          <Tooltip
                            arrow
                            title={`${user.email} ${getDisplayName(user)}`}
                          >
                            <Typography
                              noWrap
                              width={
                                selectRef?.current ? maxWidth - 50 : "auto"
                              }
                            >
                              {`${user.email} ${getDisplayName(user)}`}
                            </Typography>
                          </Tooltip>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <TextField
                  value={ticket.thirdPartyTicketId ?? ""}
                  fullWidth
                  label={t("Third Party Ticket ID")}
                  disabled={disabled}
                  onChange={(e) => {
                    dispatchTicket({ thirdPartyTicketId: e.target.value });
                    dispatchHasChanged(true);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        sx={{
          flex: "1"
        }}>
        <Grid container spacing={2} sx={{ width: '100%', flexDirection: "column" }}>
          <Grid>
            <TextField
              value={ticket.details?.requester ?? ""}
              fullWidth
              label={t("Ticket Created By")}
              disabled={true}
            />
          </Grid>
          <Grid>
            <StyledTypo size="smaller">{t("Time Of Issue")}</StyledTypo>
            <ConsoleLog>
              <Typography realTime>{timeOfIssueConverted}</Typography>
            </ConsoleLog>
          </Grid>
          <Grid>
            <StyledTypo size="smaller">{t("Ticket Created")}</StyledTypo>
            <ConsoleLog>
              <Typography realTime>
                {displayDateTime(ticket.openDate)}
              </Typography>
            </ConsoleLog>
          </Grid>
          <Grid>
            <StyledTypo size="smaller">{t("Ticket Modified")}</StyledTypo>
            <ConsoleLog>
              <Typography realTime>
                {displayDateTime(ticket.lastUpdatedDate)}
              </Typography>
            </ConsoleLog>
          </Grid>
          {ticket.details && ticket.details.closedBy && (
            <Grid>
              <TextField
                value={ticket.details?.closedBy ?? ""}
                fullWidth
                label={t("Ticket Closed By")}
                disabled={true}
              />
            </Grid>
          )}
          {ticket.triggerName && (
            <Grid>
              <StyledTypo size="smaller">{t("Trigger")}</StyledTypo>
              <ConsoleLog>
                <Typography realTime>{ticket.triggerName}</Typography>
              </ConsoleLog>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid
        sx={{
          "&.MuiGrid-item": {
            marginLeft: 8,
          },
        }}
        size={{
          md: 1
        }}></Grid>
    </Grid>
  );
};

export default TicketAdvancedDetails;
